import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Mail, Phone, MapPin } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { BRAND } from "../lib/brand";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact-messages", form);
      setDone(true);
      toast.success("Message sent!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full px-4 py-3 bg-white border border-slate-200 rounded-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/20 text-sm";
  const label = "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2";

  return (
    <div data-testid="contact-page">
      <section className="bg-[#1E2A78] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700] mb-3">Contact</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight">Let's talk shipping.</h1>
          <p className="mt-6 text-slate-200 max-w-2xl">Quote requests, customs questions, partnership opportunities — we respond within one business day.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#F1F5F9] border border-slate-200 p-6 rounded-sm">
              <div className="flex items-center gap-3 text-[#1E2A78] mb-2">
                <Phone className="h-5 w-5" />
                <div className="text-xs font-semibold uppercase tracking-wider">Call us</div>
              </div>
              <a href={BRAND.phoneHref} className="text-xl font-display font-bold text-[#0F172A] hover:text-[#1E2A78]" data-testid="contact-phone-link">{BRAND.phone}</a>
            </div>
            <div className="bg-[#F1F5F9] border border-slate-200 p-6 rounded-sm">
              <div className="flex items-center gap-3 text-[#1E2A78] mb-2">
                <Mail className="h-5 w-5" />
                <div className="text-xs font-semibold uppercase tracking-wider">Email</div>
              </div>
              <a href={`mailto:${BRAND.email}`} className="text-base font-display font-semibold text-[#0F172A] hover:text-[#1E2A78] break-all" data-testid="contact-email-link">{BRAND.email}</a>
            </div>
            <div>
              <div className="flex items-center gap-3 text-[#1E2A78] mb-3">
                <MapPin className="h-5 w-5" />
                <div className="text-xs font-semibold uppercase tracking-wider">Offices</div>
              </div>
              <div className="space-y-3">
                {BRAND.offices.map((o) => (
                  <div key={o.city} className="p-4 border border-slate-200 rounded-sm">
                    <div className="font-display font-bold text-[#0F172A]">{o.city}, {o.region}</div>
                    <div className="text-sm text-slate-500">{o.country}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-white border border-slate-200 p-10 rounded-sm text-center" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-[#1E2A78] mx-auto mb-4" />
                <h2 className="text-2xl font-display font-bold">Message sent</h2>
                <p className="mt-2 text-slate-600">We'll be in touch within one business day.</p>
                <button onClick={() => { setDone(false); setForm({ full_name: "", email: "", phone: "", subject: "", message: "" }); }} className="mt-6 px-5 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200]">
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-slate-200 p-8 rounded-sm space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={label}>Full Name</label>
                    <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={input} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className={label}>Phone (optional)</label>
                    <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} data-testid="contact-phone" />
                  </div>
                </div>
                <div>
                  <label className={label}>Email</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="contact-email" />
                </div>
                <div>
                  <label className={label}>Subject</label>
                  <input required value={form.subject} onChange={(e) => set("subject", e.target.value)} className={input} data-testid="contact-subject" />
                </div>
                <div>
                  <label className={label}>Message</label>
                  <textarea required rows="6" value={form.message} onChange={(e) => set("message", e.target.value)} className={input} data-testid="contact-message" />
                </div>
                <button type="submit" disabled={submitting} className="w-full px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] disabled:opacity-60" data-testid="contact-submit">
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
